import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { getWhatsAppLink, trackWhatsAppClick } from "@/lib/analytics";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { MessageCircle, Instagram, Loader2 } from "lucide-react";

const BUSINESS_TYPES = [
  "Restoran & Kafe",
  "Klinik & Estetik",
  "Emlak",
  "E-ticaret",
  "Güzellik & Bakım",
  "Yerel Hizmetler",
  "Diğer",
];

export default function ContactSection({ content }) {
  const [ref, isVisible] = useScrollReveal();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", business_type: "", message: "" });

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast({ variant: "destructive", title: "Eksik bilgi", description: "Lütfen ad ve telefon alanlarını doldurun." });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      toast({ title: "Talebiniz alındı", description: "En kısa sürede sizinle iletişime geçeceğiz." });
      setForm({ name: "", phone: "", email: "", business_type: "", message: "" });
    } catch (err) {
      toast({ variant: "destructive", title: "Bir hata oluştu", description: "Lütfen daha sonra tekrar deneyin ya da WhatsApp'tan yazın." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="iletisim" className="relative overflow-hidden py-24 md:py-32" data-testid="contact-cta-section">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-yellow-600/[0.04] to-transparent" />
      <div ref={ref} className={`reveal relative mx-auto max-w-7xl px-6 md:px-12 ${isVisible ? "is-visible" : ""}`}>
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-normal tracking-tight text-white sm:text-3xl lg:text-4xl">
              {content.cta_title}
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-slate-400">
              {content.cta_subtitle}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={getWhatsAppLink(content.whatsapp_number)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick("contact_cta")}
                className="flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-black transition-colors hover:bg-gold-light"
                data-testid="contact-whatsapp-cta"
              >
                <MessageCircle className="h-5 w-5" />
                WhatsApp'tan Yaz
              </a>
              <a
                href="https://instagram.com/contenthousetr"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-8 py-4 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                data-testid="contact-instagram-cta"
              >
                <Instagram className="h-5 w-5" />
                Instagram'da İncele
              </a>
            </div>
            <p className="mt-4 flex items-center gap-2 text-sm text-slate-400">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              Genellikle 1 saat içinde yanıtlıyoruz
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-2xl border border-white/5 bg-white/[0.02] p-8 backdrop-blur-xl"
            data-testid="contact-form"
          >
            <h3 className="text-xl font-medium text-white">Size ulaşalım</h3>
            <p className="mt-1 text-sm text-slate-400">Bilgilerinizi bırakın, biz sizi arayalım.</p>

            <div className="mt-6 space-y-4">
              <Input
                placeholder="Ad Soyad"
                value={form.name}
                onChange={handleChange("name")}
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-gold"
                data-testid="contact-form-name-input"
              />
              <Input
                placeholder="Telefon"
                value={form.phone}
                onChange={handleChange("phone")}
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-gold"
                data-testid="contact-form-phone-input"
              />
              <Input
                placeholder="E-posta (opsiyonel)"
                type="email"
                value={form.email}
                onChange={handleChange("email")}
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-gold"
                data-testid="contact-form-email-input"
              />
              <Select value={form.business_type} onValueChange={(v) => setForm((f) => ({ ...f, business_type: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="contact-form-business-type-select">
                  <SelectValue placeholder="İşletme türü" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                placeholder="Mesajınız (opsiyonel)"
                value={form.message}
                onChange={handleChange("message")}
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-gold"
                rows={3}
                data-testid="contact-form-message-input"
              />
              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-gold text-black hover:bg-gold-light"
                data-testid="contact-form-submit-button"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gönder"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
